import React, { useContext, useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Breadcrumb, RippleButton, Title, Switcher } from '../../Components/Atoms';
import { StoreContext } from '../../Library/contexts';
import { getDrivers, getRouteStats, getRoutes, getRouteTrips } from '../../APIs';
import PATHS from '../../Routes/paths';
import { DatePicker } from '../../Components/Molecules';
import { getISODate, secondsToHms, stampToDate, formatTimestamp } from '../../Library/datetime';
import { DATE_RANGES } from '../../Library/constants';
import "./style.css";
import { ArrowRightIcon } from '../../Assets/images/svgs';
import { Button } from '@chakra-ui/react';

const DriverRouteTrips = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const driverId = searchParams.get("driverId");
    const routeId = searchParams.get("routeId");

    const { filters, userVehicles, allVehicleRoutes, setAllVehicleRoutes, driverManagementCache, setDriverManagementCache } = useContext(StoreContext);
    const [driverDetails, setDriverDetails] = useState(null);
    const [driverTrips, setDriverTrips] = useState([]);
    const [displayedTrips, setDisplayedTrips] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    const routeName = allVehicleRoutes?.find(r => String(r.idx) === String(routeId))?.nm || "Route Trips";

    const breadcrumbPaths = [
        { idx: 1, name: "Driver Management", link: PATHS.DRIVER_MANAGEMENT },
        { idx: 2, name: driverDetails?.nm || "Driver Details", link: -1 },
        { idx: 3, name: routeName, isCurrentPage: true },
    ];

    // 1. Fetch Driver Details (evDrivers collection, same as DriverRouteSummary)
    useEffect(() => {
        if (!driverId) return;
        getDrivers((err, data) => {
            if (err) console.error(err);
            else setDriverDetails((data || []).find(d => String(d.idx) === String(driverId)) || null);
        });
    }, [driverId]);

    const formatTimeRange = (startTs, endTs) => {
        if (!startTs) return "N/A";

        // Helper to safely format timestamp (handles Number, String number, and ISO String)
        const safeFormat = (ts) => {
            if (ts === null || ts === undefined) return null;
            const d = new Date(isNaN(Number(ts)) ? ts : Number(ts));
            if (isNaN(d.getTime())) return null;
            return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
        };

        const start = safeFormat(startTs) || String(startTs);
        
        let end = start;
        if (endTs !== undefined && endTs !== null && endTs !== "") {
             const formatted = safeFormat(endTs);
             end = formatted || String(endTs); // Fallback to raw value if format fails
        }
        
        return `${start} - ${end}`;
    }

    const getScoreColor = (score) => {
        if (score > 70) return "#6AB258"; // Excellent
        if (score > 35) return "#F9793E"; // Average
        return "#D34747"; // Poor
    };

    // 2. Fetch Data (Requests + Stats)
    useEffect(() => {
        if (!driverDetails) return;

        setLoading(true);
        let selectedDate = DATE_RANGES[filters.range];

        // Fetch known vehicles for Reg lookup (use master vehicle list; evDrivers have no vehicle array)
        const vidMap = {};
        (userVehicles || []).forEach(v => { vidMap[v.idx] = v.reg; });

        const cacheKey = `${driverId}_${routeId}_${filters.range}`;
        if (driverManagementCache.trips?.[cacheKey]) {
            setDriverTrips(driverManagementCache.trips[cacheKey]);
            setLoading(false);
            return;
        }

        // Fetch Trip Requests (Source of Truth for "All Trips")
        getRouteTrips({ 
            driverId: driverId, 
            routeId: routeId, 
            from: selectedDate.from, 
            to: selectedDate.to 
        }, (err, tripRequests) => {
             if (err) {
                 console.error("Error fetching trip requests", err);
                 setLoading(false);
                 return;
             }

             if (!tripRequests || !tripRequests.length) {
                 setDriverTrips([]);
                 setLoading(false);
                 return;
             }
            
             // Fetch Route Stats to enrich (only gets trips with movement)
             const vids = [...new Set(tripRequests.map(t => t.vid))];
             
             getRouteStats({ 
                 fromTs: selectedDate.from, 
                 toTs: selectedDate.to, 
                 vids: vids 
             }, (statErr, routeStats) => {
                setLoading(false);

                 const enrichedTrips = tripRequests.map(req => {
                     // Default values from request
                     // Default values from request
                     let trip = {
                         ...req,
                         startTs: req.startTs || req.sts || req.ts || req.start_ts, // Added 'sts' fallback
                         endTs: req.endTs || req.ets || req.end_ts, // Added 'ets' fallback
                         status: (req.status === 1 || req.status === 'INCOMPLETE') ? 'INCOMPLETE' : ((req.status === 2 || req.status === 'COMPLETED') ? 'COMPLETED' : 'INCOMPLETE'),
                         dst: 0,
                         dur: 0,
                         avgSpd: 0,
                         topSpd: 0,
                         score: 0,
                         deviated: 0
                     };

                     // Try to find matching stat
                     if (routeStats && routeStats.length) {
                        routeStats.forEach(rStat => {
                            let dayScore = rStat.dayStats ? rStat.dayStats.rideScore : 0;
                            
                            if (rStat.routes) {
                                rStat.routes.forEach(route => {
                                    if (route.routeId === req.routeId && route.tripStats) {
                                         // Set Expected Distance from Route
                                         trip.expectedDist = route.routeDst;

                                         let bestMatch = null;
                                         let minDiff = Infinity;

                                         route.tripStats.forEach(ts => {
                                             // 1. Exact Match via Request Index (New Logic)
                                             if (ts.requestIdx) {
                                                const reqDate = stampToDate(req.ts || req.startTs || req.sts);
                                                const statDate = stampToDate(ts.startTs);
                                                
                                                if(String(ts.requestIdx) === String(req.idx) && reqDate === statDate) {
                                                    bestMatch = ts;
                                                    minDiff = -1; // Found exact match
                                                }
                                                // If requestIdx present but doesn't match or dates mismatch, skip.
                                             }
                                             // 2. Fallback: DriverId + Closest Time (Old Logic for legacy data WITHOUT requestIdx)
                                             else if (minDiff !== -1 && String(ts.driverId) === String(req.driverId)) {
                                                  const diff = Math.abs((ts.startTs || 0) - (req.ts || 0));
                                                  if (diff < minDiff) {
                                                      minDiff = diff;
                                                      bestMatch = ts;
                                                  }
                                             }
                                         });

                                         const match = bestMatch;

                                         if (match) {
                                             trip.dst = match.dst;
                                             trip.odoDiff = match.odoDiff ?? null; // km, preferred over dst/1000
                                             trip.dur = match.dur;
                                             trip.avgSpd = match.avgSpd;
                                             trip.topSpd = match.topSpd;
                                             trip.deviated = match.deviated;
                                             // Only overwrite if match has valid timestamps
                                             if (match.startTs) trip.startTs = match.startTs;
                                             if (match.endTs) trip.endTs = match.endTs;
                                             trip.score = match.score || dayScore || 0;
                                             
                                             // Ensure trip object has all necessary fields for RideDetails
                                             trip.routeId = req.routeId;
                                             trip.driverId = req.driverId;
                                         }
                                    }
                                });
                            }
                        });
                     }

                     // Format keys
                     const startTs = trip.startTs || trip.ts;
                     trip.dateStr = stampToDate(startTs);

                     // Calculate Derived Status
                     let derivedStatus = 'INCOMPLETE';
                     if (trip.status === 'COMPLETED') {
                         derivedStatus = 'COMPLETED';
                     } else {
                         if (!trip.dst || trip.dst === 0) derivedStatus = 'CANCELLED';
                         else derivedStatus = 'INCOMPLETE';
                     }
                     trip.derivedStatus = derivedStatus;
                     
                     return trip;
                 });

                // Sort by startTs desc
                enrichedTrips.sort((a,b) => (b.startTs || b.ts) - (a.startTs || a.ts));

                const finalTrips = enrichedTrips.map(t => ({
                    ...t,
                    reg: vidMap[t.vid] || t.vid
                }));

                setDriverTrips(finalTrips);
                setDriverManagementCache(prev => ({
                    ...prev,
                    trips: {
                        ...prev.trips,
                        [cacheKey]: finalTrips
                    }
                }));

        });
    });

    }, [driverDetails, filters.range, driverId, routeId]);

    // 3. Filter Trips based on Tab
    useEffect(() => {
        if (activeTab === 0) { // All Trips
            setDisplayedTrips(driverTrips);
        } else if (activeTab === 1) { // Completed
            setDisplayedTrips(driverTrips.filter(t => t.derivedStatus === 'COMPLETED'));
        } else if (activeTab === 2) { // Incomplete
            setDisplayedTrips(driverTrips.filter(t => t.derivedStatus === 'INCOMPLETE'));
        } else if (activeTab === 3) { // Cancelled
            setDisplayedTrips(driverTrips.filter(t => t.derivedStatus === 'CANCELLED'));
        }
    }, [driverTrips, activeTab]);


    // Helper to group by date
    const groupedTrips = displayedTrips.reduce((acc, trip) => {
        const dateStr = stampToDate(trip.startTs || trip.ts, "DD MMM YYYY"); 
         if (!acc[dateStr]) acc[dateStr] = [];
         acc[dateStr].push(trip);
         return acc;
    }, {});

    // Sort dates desc
    const sortedDates = Object.keys(groupedTrips).sort((a, b) => new Date(b) - new Date(a));


    return (
        <section className="panel-main driver-route-trips">
             <div className="driver-summary-header">
                <Breadcrumb paths={breadcrumbPaths} />
                <div className="drt-datepicker">
                     <DatePicker customMaxDate={getISODate()} />
                </div>
             </div>

            <div className="drt-tabs">
                <Switcher 
                    options={[
                        { idx: 0, label: 'All Trips', value: 'All Trips' },
                        { idx: 1, label: 'Completed', value: 'Completed' },
                        { idx: 2, label: 'Incomplete', value: 'Incompleted' },
                        { idx: 3, label: 'Cancelled', value: 'Cancelled' }
                    ]}
                    selectedOption={activeTab}
                    handleChange={(opt) => setActiveTab(opt.idx)}
                    className="drt-switcher"
                />
            </div>

            <div className="drt-content">
                {sortedDates.length === 0 && !loading && (
                    <div className="no-data">No trips found for selected date range.</div>
                )}

                {sortedDates.map(date => (
                    <div key={date} className="drt-date-group">
                        <div className="drt-date-label">
                            {date}
                        </div>
                        {groupedTrips[date].map((trip, idx) => (
                            <article key={idx} className="card widget-group drt-card">
                                <header className="widget-header drt-card-header-row">
                                    <div className="drt-header-info">
                                        <Title variant="md">{formatTimeRange(trip.startTs, trip.endTs)}</Title>
                                        <span className="drt-separator"></span>
                                        <span className="drt-veh">{trip.reg || trip.vid}</span>
                                        {activeTab === 0 && (
                                            <span className={`drt-status-badge ${(trip.derivedStatus || 'INCOMPLETE').toLowerCase()}`}>
                                                {trip.derivedStatus === 'INCOMPLETE' ? 'Incomplete' : (trip.derivedStatus === 'CANCELLED' ? 'Cancelled' : 'Completed')}
                                            </span>
                                        )}
                                    </div>
                                    
                                    {trip.dst > 0 && (
                                        <div className="riding-score">
                                            <span className="riding-text">Riding Score</span>
                                            <div className="score-container" style={{ backgroundColor: getScoreColor(trip.score) }}>
                                                <span className="score">{trip.score}/100</span>
                                            </div>
                                        </div>
                                    )}
                                </header>

                                <div className="widget-group keyvals drt-stats-grid-container">
                                    <div className="widget-main">
                                        <header>Expected Route Dist.</header>
                                        <article>{(trip.expectedDist ? trip.expectedDist/1000 : 0).toFixed(1)} kms</article>
                                    </div>
                                    <div className="widget-main">
                                        <header>Actual Route Dist.</header>
                                        <article>{(trip.odoDiff != null ? trip.odoDiff : (trip.dst ? trip.dst/1000 : 0)).toFixed(1)} kms</article>
                                    </div>
                                    <div className="widget-main">
                                        <header>Deviated Dist.</header>
                                        <article className="text-red">{(trip.deviated ? trip.deviated/1000 : 0).toFixed(1)} kms</article>
                                    </div>
                                    <div className="widget-main">
                                        <header>Total Duration</header>
                                        <article>{secondsToHms(trip.dur || trip.duration || 0)}</article>
                                    </div>
                                    <div className="widget-main">
                                        <header>Avg Speed</header>
                                        <article>{trip.avgSpd || 0} km/hr</article>
                                    </div>
                                    <div className="widget-main">
                                        <header>Top Speed</header>
                                        <article>{trip.topSpd || 0} km/hr</article>
                                    </div>
                                </div>

                                {trip.dst > 0 && (
                                    <div className="widget-link">
                                     <Button 
                                        variant="ghost" 
                                        className="driver-trip-button"
                                        rightIcon={<ArrowRightIcon />}
                                        onClick={() => navigate(`${PATHS.DRIVER_MANAGEMENT}/${PATHS.DRIVER_RIDE_DETAILS}?reg=${trip.reg}&route=${trip.routeId}&ts=${trip.startTs}&vid=${trip.vid}&source=driver_trips&driverId=${driverId}`, {
                                            state: { 
                                                ride: {
                                                    ...trip,
                                                    sdt: trip.startTs ? (trip.startTs / 1000) : 0, // RideDetails expects sdt in seconds
                                                    edt: (trip.endTs || trip.startTs) ? ((trip.endTs || trip.startTs) / 1000) : 0,   // RideDetails expects edt in seconds
                                                    vid: trip.vid,      // Ensure vid is in ride object
                                                    routeDst: trip.expectedDist // RideDetails expects routeDst
                                                }
                                            } 
                                        })}
                                     >
                                         View Trip
                                     </Button>
                                </div>
                                )}
                            </article>
                        ))}
                    </div>
                ))}
            </div>
        </section>
    );
};
export default DriverRouteTrips;
